<template>
  <BasicCard :title="tableTitle" :headerClass="headerClass">
    <template #prepend>
      <div class="pa-2 d-flex flex-wrap gap-2">
        <v-select
          v-if="showWalletFilter"
          v-model="selectedWalletId"
          :items="walletsList"
          min-width="260"
          :loading="walletsLoading"
          :label="$t('uiWalletHistory.wallet_filter')"
          item-title="title"
          item-value="id"
          prepend-inner-icon="mdi-wallet"
          variant="outlined"
          density="compact"
          @update:model-value="onWalletChange"
        >
          <template #item="{ item, props }">
            <v-list-item v-bind="props" :title="item.raw.title">
              <v-list-item-subtitle>
                ({{ item.raw.currency_id }} {{ item.raw.balance }})
              </v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-select>

        <v-select max-width="460px"
          v-model="selectedOperationType"
          :items="operationTypeItems"
          :loading="operationTypesLoading"
          :label="$t('uiWalletHistory.operation_type_filter')"
          item-title="title"
          item-value="value"
          clearable
          density="compact"
          variant="outlined"
          prepend-inner-icon="mdi-filter"
          @update:model-value="onOperationTypeChange"
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #prepend>
                <v-icon :color="item.raw.color">
                  {{ item.raw.icon }}
                </v-icon>
              </template>
            </v-list-item>
          </template>
          <template #selection="{ item }">
            <div class="d-flex align-center">
              <v-icon :color="item.raw.color" size="small" class="mr-2">
                {{ item.raw.icon }}
              </v-icon>
              <span>{{ item.title }}</span>
            </div>
          </template>
        </v-select>
      </div>
    </template>
    <v-data-table-server
      v-model:options="options"
      :headers="headers"
      :items="items"
      :itemsLength="total"
      :loading="loading"
      :data-loading="loading ? 'true' : undefined"


      :itemsPerPage="noPagination ? -1 : options.itemsPerPage"
      :itemsPerPageOptions="noPagination ? undefined : [
        { value: 5, title: '5' },
        { value: 10, title: '10' },
        { value: 15, title: '15' },
        { value: 25, title: '25' },
        { value: 50, title: '50' },
        { value: 100, title: '100' },
      ]"
    >
      <template #item="{ item }">
        <tr :style="{ backgroundColor: item.positive ? rowColorPositive : rowColorNegative }">
          <td>
            <div class="d-flex">
              <v-icon :color="item.operationColor">
                {{ item.operationIcon }}
              </v-icon>
              <div class="d-inline-block ml-2">
                <div class="text-caption">
                  {{ $t(item.operationType) }}
                </div>
                <div class="text-caption text--secondary">
                  {{ item.comment }}
                </div>
              </div>
            </div>
          </td>
          <td>
            <div class="text-wrap">
              <span
                class="text-caption text-no-wrap font-weight-bold me-2"
                :style="{ color: item.positive ? amountColorPositive : amountColorNegative }"
              >
                {{ item.amount }}
              </span>
              <span    :style="{ color: item.positive ? amountColorPositive : amountColorNegative }" class="text-caption">{{ item.currency }}</span>
            </div>
          </td>
          <td>
            <span
              v-if="item.date"
              class="font-weight-bold text-caption text-no-wrap"
            >
              {{ item.date }}
            </span>
            &nbsp;
            <span
              v-if="item.time"
              class="text--secondary text-caption text-no-wrap"
            >{{ item.time }}</span>
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </BasicCard>
</template>

<script>

import { defaults } from './WalletHistory.ts';


import { useAccountPropertyCache, useAwaitAccountId, useAwaitValue, useLocalization, useWallet } from '@/composables';
import { useEventBus } from '@/pinia/useEventBus';
import { wearMaskMoney } from '@/utils/money';
import { useWalletApi } from '@ui-api3-sdk/api/api3';
import { mapState } from 'pinia';

import BasicCard from './base/BasicCard.vue';


const { lt, la, d } = useLocalization('uiWalletHistory');

function getOperationObject (opt) {

  const isTransfer = /transfer/.test(opt);

  if (/^in:/.test(opt)) {
    return {
      operationType: operationCode(opt.slice(4)),
      operationIcon: isTransfer ? 'mdi-swap-horizontal-bold' : 'mdi-import',
      operationColor: isTransfer ? 'primary' : 'success',
    };
  }

  if (/^out:/.test(opt)) {
    return {
      operationType: operationCode(opt.slice(5)),
      operationIcon: isTransfer ? 'mdi-swap-horizontal-bold' : 'mdi-export-variant',
      operationColor: isTransfer ? 'primary' : 'warning',
    };
  }

  return {
    operationType: operationCode(opt),
    operationIcon: 'mdi-mdi-swap-horizontal-bold',
    operationColor: 'primary',
  };
}

function validatorDate (value) {
  // format 2021-10-01
  return /^[1-2][0-9][0-9][0-9]-[0-1][0-9]-[0-3][0-9]$/.test(value);
}

function operationCode (value) {
  switch (value) {
    case 'Online fill-up':
      return la('op_online_fill_up');
    case 'Adjustment':
      return la('op_adjustment');
    case 'Commission payment':
      return la('op_commission_payment');
    case 'Payout':
      return la('op_payout');
    case 'Account to account transfer':
      return la('op_account_to_account_transfer');
    case 'Administrative transfer':
      return la('op_administrative_transfer');
    case 'Purchase order':
      return la('op_purchase_order');
    case 'Purchase cancellation':
      return la('op_purchase_cancellation');
    case 'Wallet exchange':
      return la('op_wallet_exchange');
    default:
      return value;
  }
}

export default {
  name: 'WalletHistory',

  components: {
    BasicCard,
  },

  props: {

    id: {
      type: String,
      default: undefined,
    },

    operationTypes: {
      type: Array,
      default() {
        return [];
      },

      validator(val) {
        return val.every(item => typeof item === 'number');
      },
    },

    // [[country_id, wallet_id | wallet_ids[]], ...]
    walletCountries: {
      type: Array,
      default() {
        return [];
      },

      validator(val) {
        return val.every(item => {
          if (!Array.isArray(item) || item.length !== 2) return false;
          if (typeof item[0] !== 'string') return false;
          // Second element can be a number or array of numbers
          if (typeof item[1] === 'number') return true;
          if (Array.isArray(item[1])) {
            return item[1].every(id => typeof id === 'number');
          }
          return false;
        });
      },
    },

    start: {
      type: String,
      validator: validatorDate,
      default: null,
    },

    end: {
      type: String,
      validator: validatorDate,
      default: null,
    },

    wallet: {
      type: [Number, Array],
      default: null,
      validator(val) {
        if (val === null) return true;
        if (typeof val === 'number') return true;
        if (Array.isArray(val)) {
          return val.every(item => typeof item === 'number');
        }
        return false;
      },
    },

    title: {
      type: String,
      default: '',
    },

    headerClass: {
      type: String,
      default: defaults.headerClass,
    },

    noPagination: {
      type: Boolean,
      default: false,
    },

    rowColorPositive: {
      type: String,
      default: defaults.rowColorPositive,
    },

    rowColorNegative: {
      type: String,
      default: defaults.rowColorNegative,
    },

    amountColorPositive: {
      type: String,
      default: defaults.amountColorPositive,
    },

    amountColorNegative: {
      type: String,
      default: defaults.amountColorNegative,
    },
  },

  data: () => ({
    // isGrid: true,
    loading: false,
    currencyId: '',
    walletName: '',
    items: [],
    aId: null,
    options: {},
    total: 0,
    walletFetched: false,
    walletId: null,
    // Operation types filter
    operationTypeItems: [],
    operationTypesLoading: false,
    selectedOperationType: null,
    operationTypesFetched: false,
    // Wallet filter
    walletsList: [],
    walletsLoading: false,
    selectedWalletId: null,
  }),

  computed: {

    ...mapState(useEventBus, ['walletHistoryUpdated']),

    headers() {
      return [
        {
          value: 'operation',
          title: lt('operation'),
          sortable: false,
          key: 'operation',
        },
        {
          value: 'amount',
          title: lt('amount'),
          sortable: false,
          key: 'amount',
        },
        {
          value: 'date',
          title: lt('date'),
          sortable: false,
          key: 'date',
        },
      ];
    },

    tableTitle() {
      return this.title || lt('operation_history_for', { n1: this.walletName || lt('loading') });
    },

    // Show wallet filter only if multiple wallets are provided
    showWalletFilter() {
      // Check if wallet prop has multiple wallets
      if (Array.isArray(this.wallet) && this.wallet.length > 1) {
        return true;
      }
      // Check if walletCountries will provide multiple wallets
      // (we can't check here synchronously, so we rely on walletsList)
      return this.walletsList.length > 1;
    },

    // Get effective wallet IDs to work with
    effectiveWalletIds() {
      if (Array.isArray(this.wallet)) {
        return this.wallet.length > 0 ? this.wallet : null;
      }
      if (typeof this.wallet === 'number') {
        return [this.wallet];
      }
      return null;
    },
  },

  watch: {
    options: {
      handler() {
        // Only fetch if wallet is already selected (prevents premature fetch)
        if (this.showWalletFilter && !this.selectedWalletId) {
          return;
        }
        this.fetch();
      },

      deep: true,
    },

    walletHistoryUpdated() {
      this.fetch();
    },
  },

  async mounted() {
    // Initialize operation types loading
    this.fetchOperationTypes();

    // Check if we need to show wallet filter
    // This includes: multiple wallets in wallet prop OR valid walletCountries with potential multiple wallets
    const needsWalletsList = (Array.isArray(this.wallet) && this.wallet.length > 1) ||
                              this.hasValidWalletCountries();

    if (needsWalletsList) {
      await this.fetchWalletsList();
    }
  },

  methods: {

    // Helper to check if walletCountries has valid data
    hasValidWalletCountries() {
      if (!Array.isArray(this.walletCountries) || this.walletCountries.length === 0) {
        return false;
      }

      // Check if at least one valid entry exists
      return this.walletCountries.some(item => {
        // Item must be array with 2 elements
        if (!Array.isArray(item) || item.length !== 2) return false;

        // First element (country) must be non-empty string
        if (typeof item[0] !== 'string' || item[0].trim() === '') return false;

        // Second element (wallet IDs) validation
        const walletValue = item[1];

        // Single wallet ID as number
        if (typeof walletValue === 'number' && walletValue > 0) return true;

        // Array of wallet IDs
        if (Array.isArray(walletValue)) {
          return walletValue.length > 0 &&
                 walletValue.every(id => typeof id === 'number' && id > 0);
        }

        return false;
      });
    },

    getOperationObjectLocal(opt) {
      const isTransfer = /transfer/.test(opt);

      if (/^in:/.test(opt)) {
        return {
          operationType: this.operationCodeLocal(opt), // Pass full title with direction
          operationIcon: isTransfer ? 'mdi-swap-horizontal-bold' : 'mdi-import',
          operationColor: isTransfer ? 'primary' : 'success',
        };
      }

      if (/^out:/.test(opt)) {
        return {
          operationType: this.operationCodeLocal(opt), // Pass full title with direction
          operationIcon: isTransfer ? 'mdi-swap-horizontal-bold' : 'mdi-export-variant',
          operationColor: isTransfer ? 'primary' : 'warning',
        };
      }

      return {
        operationType: this.operationCodeLocal(opt),
        operationIcon: 'mdi-swap-horizontal-bold',
        operationColor: 'primary',
      };
    },

    filterComment(comment) {
      // Filter out technical strings and empty values
      if (!comment ||
          typeof comment !== 'string' ||
          comment.trim() === '' ||
          comment === '<string>' ||
          comment === 'null' ||
          comment === 'undefined' ||
          comment.toLowerCase() === 'string' ||
          /^<.*>$/.test(comment.trim())) { // Filter any string wrapped in angle brackets
        return '';
      }
      return comment.trim();
    },

    operationCodeLocal(value) {
      // Handle operations with direction prefixes
      if (/^in:/.test(value)) {
        const operationName = value.slice(4); // Remove "in: " prefix
        switch (operationName) {
          case 'Online fill-up':
            return this.$t('uiWalletHistory.op_online_fill_up');
          case 'Adjustment':
            return this.$t('uiWalletHistory.op_adjustment');
          case 'Commission payment':
            return this.$t('uiWalletHistory.op_commission_payment');
          case 'Account to account transfer':
            return this.$t('uiWalletHistory.op_account_to_account_transfer_in');
          case 'Administrative transfer':
            return this.$t('uiWalletHistory.op_administrative_transfer_in');
          case 'Purchase cancellation':
            return this.$t('uiWalletHistory.op_purchase_cancellation');
          case 'Wallet exchange':
            return this.$t('uiWalletHistory.op_wallet_exchange_in');
          default:
            return operationName;
        }
      }

      if (/^out:/.test(value)) {
        const operationName = value.slice(5); // Remove "out: " prefix
        switch (operationName) {
          case 'Adjustment':
            return this.$t('uiWalletHistory.op_adjustment');
          case 'Payout':
            return this.$t('uiWalletHistory.op_payout');
          case 'Account to account transfer':
            return this.$t('uiWalletHistory.op_account_to_account_transfer_out');
          case 'Administrative transfer':
            return this.$t('uiWalletHistory.op_administrative_transfer_out');
          case 'Purchase order':
            return this.$t('uiWalletHistory.op_purchase_order');
          case 'Wallet exchange':
            return this.$t('uiWalletHistory.op_wallet_exchange_out');
          default:
            return operationName;
        }
      }

      // Handle operations without direction prefix (fallback)
      switch (value) {
        case 'Online fill-up':
          return this.$t('uiWalletHistory.op_online_fill_up');
        case 'Adjustment':
          return this.$t('uiWalletHistory.op_adjustment');
        case 'Commission payment':
          return this.$t('uiWalletHistory.op_commission_payment');
        case 'Payout':
          return this.$t('uiWalletHistory.op_payout');
        case 'Account to account transfer':
          return this.$t('uiWalletHistory.op_account_to_account_transfer');
        case 'Administrative transfer':
          return this.$t('uiWalletHistory.op_administrative_transfer');
        case 'Purchase order':
          return this.$t('uiWalletHistory.op_purchase_order');
        case 'Purchase cancellation':
          return this.$t('uiWalletHistory.op_purchase_cancellation');
        case 'Wallet exchange':
          return this.$t('uiWalletHistory.op_wallet_exchange');
        default:
          return value;
      }
    },

    fetchOperationTypes() {
      if (this.operationTypesFetched) return;

      // Hardcoded operation types list
      const operations = [
        {
          "id": 4,
          "title": "in: Online fill-up",
          "is_enrollment": true,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 5,
          "title": "in: Adjustment",
          "is_enrollment": true,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 6,
          "title": "out: Adjustment",
          "is_enrollment": false,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 7,
          "title": "in: Commission payment",
          "is_enrollment": true,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 8,
          "title": "out: Payout",
          "is_enrollment": false,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 9,
          "title": "in: Account to account transfer",
          "is_enrollment": true,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 10,
          "title": "out: Account to account transfer",
          "is_enrollment": false,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 11,
          "title": "in: Administrative transfer",
          "is_enrollment": true,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 12,
          "title": "out: Administrative transfer",
          "is_enrollment": false,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 13,
          "title": "out: Purchase order",
          "is_enrollment": false,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 14,
          "title": "in: Purchase cancellation",
          "is_enrollment": true,
          "is_immutable": null,
          "alias": null
        },
        {
          "id": 15,
          "title": "in: Wallet exchange",
          "is_enrollment": true,
          "is_immutable": true,
          "alias": null
        },
        {
          "id": 16,
          "title": "out: Wallet exchange",
          "is_enrollment": false,
          "is_immutable": true,
          "alias": null
        }
      ];

      this.operationTypeItems = operations.map((o) => {
        // Get operation object with icon and color
        const operationObj = this.getOperationObjectLocal(o.title);
        return {
          title: operationObj.operationType,
          value: o.id,
          icon: operationObj.operationIcon,
          color: operationObj.operationColor,
          rawTitle: o.title
        };
      });
      this.operationTypesFetched = true;
    },

    onOperationTypeChange() {
      // Reset to first page when filter changes
      if (this.options.page) {
        this.options.page = 1;
      }
      this.fetch();
    },

    onWalletChange() {
      // Reset to first page when wallet filter changes
      if (this.options.page) {
        this.options.page = 1;
      }
      // Reset wallet fetched flag to fetch new wallet params
      this.walletFetched = false;
      this.fetch();
    },

    async fetchWalletsList() {
      this.walletsLoading = true;

      try {
        let walletIds = [];
        let countryMatched = false;

        // First check if walletCountries is configured and valid
        if (this.hasValidWalletCountries()) {
          const countryProp = await useAwaitValue(
            useAccountPropertyCache('country_id'), 'prop',
          );

          const countryId = String(countryProp.raw).toLowerCase();

          if (countryId) {
            const walletCountry = this.walletCountries.find(
              wc => Array.isArray(wc) &&
                   wc.length === 2 &&
                   typeof wc[0] === 'string' &&
                   wc[0].toLowerCase() === countryId
            );

            if (walletCountry) {
              const walletValue = walletCountry[1];
              walletIds = Array.isArray(walletValue) ? walletValue : [walletValue];
              countryMatched = true;
            }
          }
        }

        // If country not matched or walletCountries not configured, use wallet prop
        if (!countryMatched && this.effectiveWalletIds && this.effectiveWalletIds.length > 0) {
          walletIds = this.effectiveWalletIds;
        }

        if (walletIds.length === 0) {
          return;
        }

        const { wallets, fetchWallets } = await import('@/composables/useWalletOperations').then(
          m => m.useWalletOperations()
        );

        // If wallets not loaded yet, fetch them
        if (wallets.value.length === 0) {
          await fetchWallets();
        }

        // Filter wallets based on walletIds
        this.walletsList = wallets.value.filter(w =>
          walletIds.includes(w.id)
        );

        if (!this.selectedWalletId && this.walletsList.length > 0) {
          this.selectedWalletId = this.walletsList[0].id;
          // Trigger fetch after setting default wallet
          this.$nextTick(() => {
            this.fetch();
          });
        }
      } catch (error) {
        console.error('Error fetching wallets:', error);
      } finally {
        this.walletsLoading = false;
      }
    },

    async fetch() {
      const { page = 1, itemsPerPage: limit = 10 } = this.options;

      // If options is empty or page/limit are not set, skip fetch
      if (!page || !limit) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const that = this;

      this.aId = await useAwaitAccountId(this.accountId);

      this.loading = true;

      if (!this.walletFetched) {
        await this.getWalletParams();
      }

      if ([undefined, null].includes(this.walletId)) {
        this.loading = false;
        return;
      }

      try {
        const operationTypeIds = this.selectedOperationType
          ? [this.selectedOperationType, ...this.operationTypes]
          : this.operationTypes;

        await useWalletApi().getOperations({
          id: this.aId,
          walletId: this.walletId,
          limit,
          page  : page - 1,
          operationTypeIds,
          start: this.start || undefined,
          end: this.end || undefined,
        }, {
          headers: {
            'Locale': 'en',
          },
        })
          .then((res) => {
            const { list:items, total } = res.data.payload;

            this.total = total;

            this.items = items.map(item => {
              const dateObj = new Date(item.date);

              const date = d(dateObj, 'medium');
              const time = d(dateObj, {
                hour: '2-digit',
                minute: '2-digit',
              });

              return {
                ...(getOperationObject.call(that, item.walletOperationType.title)),
                comment: that.filterComment(item.comment),
                date,
                time,
                amount: `${wearMaskMoney(item.amount.split('.').join(''))}`,
                currency: item.currency,
                positive: parseFloat(item.amount) > 0,
              };
            });
          });
      } finally {
        this.loading = false;
      }
    },

    async getWalletParams() {
      let countryMatched = false;

      // Wallet countries logic - check FIRST if configured and valid
      if (this.hasValidWalletCountries()) {
        const countryProp = await useAwaitValue(
          useAccountPropertyCache('country_id'), 'prop',
        );

        const countryId = String(countryProp.raw).toLowerCase();

        if (countryId) {
          const walletCountry = this.walletCountries.find(
            wc => Array.isArray(wc) &&
                 wc.length === 2 &&
                 typeof wc[0] === 'string' &&
                 wc[0].toLowerCase() === countryId
          );

          if (walletCountry) {
            countryMatched = true;
            const walletValue = walletCountry[1];
            // If wallet value is array of multiple wallets, treat it like multi-wallet mode
            if (Array.isArray(walletValue)) {
              // If multiple wallets and filter is shown, use selected wallet
              if (walletValue.length > 1 && this.selectedWalletId) {
                this.walletId = this.selectedWalletId;
              } else if (walletValue.length > 0) {
                // Single wallet in array or no selection yet
                this.walletId = walletValue[0];
              }
            } else {
              // Single wallet as number
              this.walletId = +walletValue;
            }
          }
        }
      }

      // If country not matched, use wallet prop
      if (!countryMatched) {
        // Multi-wallet mode: use selected wallet from filter
        if (this.showWalletFilter && this.selectedWalletId) {
          this.walletId = this.selectedWalletId;
        }
        // Single wallet mode (number or array with one element)
        else if (this.effectiveWalletIds && this.effectiveWalletIds.length === 1) {
          this.walletId = this.effectiveWalletIds[0];
        }
      }

      if (!this.walletId) {
        // In multi-wallet mode without selection, don't throw error, just don't load
        if (this.showWalletFilter) {
          this.walletFetched = true;
          return;
        }
        throw new Error('wallet is not specified or not matching current country');
      }

      const wallet = await useAwaitValue(
        useWallet(this.aId, this.walletId), 'currentWallet',
      );

      this.currencyId = wallet.currency_id;
      this.walletName = wallet.title;

      this.walletFetched = true;
    },
  },
};
</script>

<style lang="scss" module>
.header {
  font-size: 14px;
  font-weight: 300;
  text-transform: uppercase;
}
</style>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
